import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What is the fastest treatment for severe depression?",
    answer:
      "The fastest FDA-cleared depression treatment is SAINT® accelerated TMS, which delivers 50 sessions in 5 consecutive days. In the open-label study, average time to remission was 2.6 days. ECT can also produce rapid improvement for severe or psychotic depression but requires anesthesia. SPRAVATO® (esketamine) can produce improvement within hours to days. Fast does not mean best for everyone — a psychiatric evaluation determines the right option.",
  },
  {
    question: "How fast does SAINT TMS work?",
    answer:
      "The SAINT® protocol is delivered over 5 consecutive days. In the open-label study, average time to remission was 2.6 days. In the sham-controlled trial, 78.6% of the active group reached remission at 4 weeks. The studies were small and from a single site. Individual results vary, and no outcome is guaranteed.",
  },
  {
    question:
      "What is the newest treatment for treatment-resistant depression?",
    answer:
      "Recent regulatory milestones include SAINT® (FDA-cleared September 2022), SPRAVATO® as monotherapy for TRD (FDA-approved January 2025), and intermittent theta burst stimulation (FDA-cleared 2018). Newer does not mean better for every person — each has specific indications, risks, and evidence limits.",
  },
];

export const pageFastestTreatment: LearnPageData = {
  slug: "fastest-treatment-for-severe-depression",
  title: "Fastest Treatment for Severe Depression | Neuro Wellness",
  description:
    "The fastest FDA-cleared depression treatment is SAINT® (5 days). Compare SAINT, SPRAVATO, ECT, and TMS by speed, evidence, and eligibility.",
  canonical: "/learn/fastest-treatment-for-severe-depression",
  h1: "What Is the Fastest Treatment for Severe Depression?",
  breadcrumbLabel: "Fastest Treatment for Severe Depression",
  crumbs: baseCrumbs("Fastest Treatment for Severe Depression"),
  shortAnswer:
    "The fastest FDA-cleared depression treatment is SAINT® accelerated TMS, which delivers 50 sessions in 5 consecutive days. In the open-label study, average time to remission was 2.6 days. ECT can also produce rapid improvement for severe or psychotic depression but requires anesthesia. SPRAVATO® (esketamine) can produce improvement within hours to days. Fast does not mean best for everyone — a psychiatric evaluation determines the right option.",
  keyTakeaways: [
    "SAINT® delivers 50 sessions in 5 consecutive days — the fastest FDA-cleared course.",
    "Average time to remission in the SAINT open-label study was 2.6 days.",
    "ECT can produce rapid improvement but requires anesthesia.",
    "SPRAVATO® can produce improvement within hours to days.",
    "Fast does not mean best for everyone.",
  ],

  sections: [
    {
      heading: "SAINT® — 5 Days",
      body: (
        <>
          {P(
            "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) delivers 10 intermittent theta burst sessions per day for 5 consecutive days, with an fMRI-guided target. It is FDA-cleared for adults with major depressive disorder who have not improved on antidepressants. In the open-label study, average time to remission was 2.6 days. The sham-controlled trial showed 78.6% remission at 4 weeks. Studies were small and single-site.",
          )}
          {P(
            <>
              <a
                href="/learn/how-fast-does-saint-tms-work"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Learn more about how fast SAINT works
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "SPRAVATO® — Hours to Days",
      body: (
        <>
          {P(
            "SPRAVATO® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression. Some patients notice improvement within hours to days of the first session. Treatment requires 2 hours of in-clinic monitoring and a driver home. It is given twice weekly for 4 weeks, then weekly, then every 1 to 2 weeks.",
          )}
        </>
      ),
    },
    {
      heading: "ECT — Rapid for Severe Depression",
      body: (
        <>
          {P(
            "Electroconvulsive therapy can produce rapid improvement for severe, psychotic, or life-threatening depression. It requires general anesthesia and a hospital or outpatient surgical setting. ECT has the highest remission rates for severe depression but carries cognitive risks. It is generally reserved for the most severe cases.",
          )}
        </>
      ),
    },
    {
      heading: "Conventional TMS — 6 Weeks",
      body: (
        <>
          {P(
            "Standard TMS delivers one session per weekday for about 6 weeks (30 to 36 sessions). It is FDA-cleared, non-invasive, and broadly covered by insurance. It is slower than SAINT® but has a larger evidence base and broader insurance coverage.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Cole EJ, et al. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
    {
      citation: "Cole EJ, et al. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/fastest-treatment-for-severe-depression#webpage`,
        name: "Fastest Treatment for Severe Depression",
        url: `${SITE_URL}/learn/fastest-treatment-for-severe-depression`,
        description: "Fastest treatments for severe depression compared.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/fastest-treatment-for-severe-depression#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Fastest Treatment for Severe Depression")),
    ],
  },
  relatedLinks: [
    {
      title: "How Fast Does SAINT® Work?",
      href: "/learn/how-fast-does-saint-tms-work",
      description: "SAINT timeline and evidence",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "SPRAVATO®",
      href: "/services/spravato",
      description: "FDA-approved esketamine",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
